import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertTriangle, HelpCircle } from 'lucide-react';

const ConfirmLoanRead = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  
  const status = searchParams.get('status');
  const loanNumber = searchParams.get('loan_number');
  const customerName = searchParams.get('customer_name');
  const token = searchParams.get('token');

  useEffect(() => {
    // Simulate loading for better UX
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const getStatusContent = () => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />,
          title: 'Confirmação Realizada!',
          message: 'Obrigado por confirmar o recebimento do email do empréstimo. Sua confirmação foi registrada com sucesso em nosso sistema.',
          bgGradient: 'from-green-400 to-emerald-600',
          cardBorder: 'border-green-200',
          showLoanInfo: true
        };
      case 'invalid_token':
        return {
          icon: <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />,
          title: 'Link Inválido',
          message: 'O link de confirmação não é válido ou expirou. Por favor, entre em contato conosco se precisar de ajuda.',
          bgGradient: 'from-red-400 to-red-600',
          cardBorder: 'border-red-200',
          showLoanInfo: false
        };
      case 'not_found':
        return {
          icon: <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />,
          title: 'Empréstimo Não Encontrado',
          message: 'Não foi possível encontrar o empréstimo associado a este token. Por favor, entre em contato conosco se precisar de ajuda.',
          bgGradient: 'from-amber-400 to-orange-600',
          cardBorder: 'border-amber-200',
          showLoanInfo: false
        };
      case 'error':
        return {
          icon: <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />,
          title: 'Erro Interno',
          message: 'Ocorreu um erro ao processar sua confirmação. Tente novamente mais tarde ou entre em contato conosco se o problema persistir.',
          bgGradient: 'from-red-400 to-red-600',
          cardBorder: 'border-red-200',
          showLoanInfo: false
        };
      default:
        return {
          icon: <HelpCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />,
          title: 'Status Desconhecido',
          message: 'Não foi possível determinar o status da confirmação. Por favor, entre em contato conosco.',
          bgGradient: 'from-gray-400 to-gray-600',
          cardBorder: 'border-gray-200',
          showLoanInfo: false
        };
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Processando confirmação...</p>
        </Card>
      </div>
    );
  }

  const content = getStatusContent();

  return (
    <div className={`min-h-screen bg-gradient-to-br ${content.bgGradient} flex items-center justify-center p-4`}>
      <Card className={`w-full max-w-lg p-8 text-center shadow-2xl ${content.cardBorder} border-2`}>
        {content.icon}
        
        <h1 className="text-3xl font-bold text-foreground mb-4">
          {content.title}
        </h1>

        {content.showLoanInfo && loanNumber && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6 border-l-4 border-green-500">
            <div className="text-lg font-semibold text-foreground mb-1">
              Empréstimo: {decodeURIComponent(loanNumber)}
            </div>
            {customerName && (
              <div className="text-muted-foreground">
                {decodeURIComponent(customerName)}
              </div>
            )}
          </div>
        )}

        <p className="text-muted-foreground text-lg leading-relaxed mb-8">
          {content.message}
        </p>

        {status === 'success' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm font-medium">
              💡 Você pode fechar esta aba agora
            </p>
          </div>
        )}

        <Button 
          onClick={() => window.close()} 
          variant="outline"
          className="w-full"
        >
          Fechar Janela
        </Button>

        {status !== 'success' && (
          <p className="text-sm text-muted-foreground mt-4">
            Em caso de dúvidas, entre em contato com nossa equipe
          </p>
        )}
      </Card>
    </div>
  );
};

export default ConfirmLoanRead;